import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as TransactionIcon } from '../../assets/icons/sidebar/sidebar-payment.svg';
import DefaultApiLimit from '../../config/defaultLimit';
import { PowerBankPreauthInterface } from '../../interface/powerbank-preauth.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../redux/slices/PageTitleSlice';
import {
  disablePreauthFilterApplied,
  fetchAllPowerBankPreauths,
  fetchPowerBankPreauthFilters,
  setSortData,
  updatePreauthFilters,
} from '../../redux/slices/PowerBankPreauthSlice';
import CustomDateRangePicker from '../../shared-components/custom-date-range/CustomDateRangePicker';
import CustomTable from '../../shared-components/custom-table/CustomTable';
import { useTableSidePanel } from '../../shared-components/table-side-panel/hooks/useTableSidePanel';
import PowerBankPreauthSidePanel from '../../shared-components/table-side-panel/PowerBankPreauthSidePanel';
import { GetParametersFromCommonFilters } from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import { changeDateFormat, getDaysBetween, removeEmptyKeys } from '../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../shared-functions/ExportFile';
import FormatUtcString from '../../shared-functions/FormatUtcString';
import { permissionCheck } from '../../shared-functions/JWTDocodeFunction';
import ToFixedValue from '../../shared-functions/ToFixedValue';
import powerBankPreauthApi from '../../utils/api/v2/powerbank-preauth';

/**
 * Format ISO date string into standard date and time string.
 * @param {string | null | undefined} isoString - The ISO date string.
 * @returns The formatted date and time string or hyphen if null.
 */
const formatDateTime = (isoString: string | null | undefined): string => {
  if (!isoString) return '-';
  const dateString = ConvertUTCtoDate(isoString);
  const timeString = FormatUtcString(ConvertUTCtoHHMM(isoString));
  return `${dateString} ${timeString}`;
};

/**
 * Helper to render status badge with appropriate styling class.
 * @param {string | null | undefined} status - Status string.
 * @returns The status badge element.
 */
const renderStatusBadge = (status: string | null | undefined): React.JSX.Element => {
  if (!status) return <span>-</span>;
  const lowerStatus = status.toLowerCase();
  return <span className={`preauth-status-badge ${lowerStatus}`}>{status}</span>;
};

/**
 * PowerBank Request Management component.
 * @returns The rendered component.
 */
function PowerBankRequest(): React.JSX.Element {
  const { t } = useTranslation('transaction');
  const dispatch = useAppDispatch();

  const {
    preauths,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.powerBankPreauth);

  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [searchParameters, setSearchParameters] = useSearchParams();
  const pageReference = React.useRef<number>(page || 1);

  useEffect(() => {
    if (page) {
      pageReference.current = page;
    }
  }, [page]);

  const today = new Date();
  const fourteenDaysAgo = new Date();
  fourteenDaysAgo.setDate(today.getDate() - 13);
  const initialStartDate = changeDateFormat(fourteenDaysAgo.toString());
  const initialEndDate = changeDateFormat(today.toString());

  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: initialStartDate,
    endDate: initialEndDate,
  });
  const [statusCounts, setStatusCounts] = useState<{
    counts?: {
      RESERVED?: number;
      LINKED?: number;
      CAPTURED?: number;
      RELEASED?: number;
      ORPHANED?: number;
    };
    total?: number;
  }>();

  /**
   * Clears the date range values.
   */
  const clearDateRange = () => {
    setDateRange({
      startDate: '',
      endDate: '',
    });
  };

  /**
   * Handle date range change.
   * @param {string} start - The start date string.
   * @param {string} end - The end date string.
   */
  const handleDateRangeChange = (start: string, end: string) => {
    setDateRange({ startDate: start, endDate: end });
  };

  /**
   * Render the date filters.
   * @returns {React.JSX.Element} The rendered date filter elements.
   */
  const renderDateFilters = () => {
    return (
      <div className="date-content">
        <CustomDateRangePicker
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onChange={handleDateRangeChange}
          onClear={clearDateRange}
          placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
          defaultPreset="last14Days"
          disableDates="future"
        />
      </div>
    );
  };

  /**
   * Fetch status counts for PowerBank preauth payments.
   */
  const fetchStatusCounts = async (): Promise<void> => {
    try {
      const queryParameters = GetParametersFromCommonFilters(filters);
      // The status-counts API does not allow the 'status' query parameter
      delete queryParameters.status;
      const response = await powerBankPreauthApi.fetchPreauthStatusCounts(
        removeEmptyKeys({
          ...queryParameters,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
        }),
      );
      setStatusCounts(response?.data);
    } catch (error) {
      console.error('Failed to fetch preauth status counts', error);
    }
  };

  const activeStatusFilter = filters.find((f) => f.key === 'status');

  /**
   * Check if a status badge card is currently active in filters.
   * @param {string} statusKey - The status key to check.
   * @returns {boolean} True if the status is active in filters.
   */
  const isStatusCardActive = (statusKey: string): boolean => {
    if (!activeStatusFilter || !activeStatusFilter.applyFilter) {
      return false;
    }
    if (Array.isArray(activeStatusFilter.value)) {
      return activeStatusFilter.value.includes(statusKey);
    }
    return activeStatusFilter.value === statusKey;
  };

  /**
   * Handle click event on a status badge card to toggle filter.
   * @param {string} key - Filter key.
   * @param {string} value - Filter value to toggle.
   */
  const handleStatusCardClick = (key: string, value: string): void => {
    const existingFilter = filters.find((f: any) => f.key === key);
    let isActive = false;

    if (existingFilter && existingFilter.applyFilter) {
      if (Array.isArray(existingFilter.value)) {
        isActive = existingFilter.value.includes(value);
      } else {
        isActive = existingFilter.value === value;
      }
    }

    if (isActive) {
      dispatch(
        updatePreauthFilters({
          key,
          type: 'remove',
          value: '',
        }),
      );
    } else {
      dispatch(
        updatePreauthFilters({
          key,
          value,
          type: 'replace',
          valueToShow: [value],
        }),
      );
    }
  };

  /**
   * Handle click on the Total stat item to clear status filter.
   */
  const handleTotalCardClick = (): void => {
    if (activeStatusFilter?.applyFilter) {
      dispatch(
        updatePreauthFilters({
          key: 'status',
          type: 'remove',
          value: '',
        }),
      );
    }
  };

  /**
   * Fetch PowerBank preauth list via Redux action.
   * @param {any} data - Query payload for fetching preauths.
   * @param {boolean} [force] - Force fetch even if loading.
   */
  const fetchPreauths = (data: any, force = false) => {
    if (data.startDate && data.endDate && getDaysBetween(data.startDate, data.endDate) > 31) {
      dispatch(
        setNotificationMessage({
          message: t('dateRangeLimitMessage'),
          status: true,
          type: 'error',
          code: 400,
        }),
      );
      return;
    }

    if (!loading || force) {
      dispatch(fetchAllPowerBankPreauths(data));
    }
  };

  /**
   * Refresh preauth table data using current filters and sorting parameters.
   * @param {boolean} [force] - Force refresh even if loading.
   */
  const handleRefresh = (force = false) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const sortValue = sortField && sortBy ? { sortField, sortBy } : {};
    fetchPreauths(
      {
        ...queryParameters,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        page: pageReference.current || page || 1,
        limit,
        ...sortValue,
      },
      force,
    );
  };

  const { handleSidePanelClose, renderSidePanelTopLeftContent } = useTableSidePanel({
    onRefresh: handleRefresh,
    sessions: preauths,
    disableEdit: true,
    disableDelete: true,
  });

  /**
   * Column configuration for CustomTable.
   */
  const column: any = [
    {
      label: t('referenceId'),
      accessorKey: 'internalRef',
    },
    {
      label: t('pspPreauthId'),
      accessorKey: 'pspPreauthId',
      sortAllow: false,
      queryParamsKey: 'preauthId',
      sidePanelComponent: <PowerBankPreauthSidePanel fetchIdFrom="queryParams" />,
    },
    {
      label: t('paymentMethod'),
      accessorKey: 'paymentMethod',
      sortAllow: false,
    },
    {
      label: t('tokenUid'),
      accessorKey: 'tokenUid',
      sortAllow: false,
    },
    {
      label: t('amount'),
      accessorKey: 'amount',
      sortAllow: true,
      /**
       * Custom formatter for Amount.
       * @param {PowerBankPreauthInterface} row - Row data.
       * @returns The formatted amount element.
       */
      customData: (row: PowerBankPreauthInterface) => (
        <span>
          {ToFixedValue(row.amount)} {row.currency || 'SAR'}
        </span>
      ),
    },
    {
      label: t('capturedAmount'),
      accessorKey: 'capturedAmount',
      sortAllow: true,
      /**
       * Custom formatter for Captured Amount.
       * @param {PowerBankPreauthInterface} row - Row data.
       * @returns The formatted captured amount element.
       */
      customData: (row: PowerBankPreauthInterface) => (
        <span>
          {row.capturedAmount != null
            ? `${ToFixedValue(row.capturedAmount)} ${row.currency || 'SAR'}`
            : '-'}
        </span>
      ),
    },
    {
      label: t('status'),
      accessorKey: 'status',
      sortAllow: false,
      /**
       * Custom formatter for Status.
       * @param {PowerBankPreauthInterface} row - Row data.
       * @returns The status badge element.
       */
      customData: (row: PowerBankPreauthInterface) => renderStatusBadge(row.status),
    },
    // {
    //   label: t('sessionId'),
    //   accessorKey: 'cpoSessionId',
    //   queryParamsKey: 'sessionId',
    //   queryParamsValue: 'cpoSessionId',
    //   sidePanelComponent: <EmspSessionSidePanel fetchIdFrom="queryParams" />,
    //   sortAllow: false,
    // },
    // {
    //   label: t('locationId'),
    //   accessorKey: 'locationId',
    //   sortAllow: false,
    // },
    // {
    //   label: t('evseUid'),
    //   accessorKey: 'evseUid',
    //   sortAllow: false,
    // },
    // {
    //   label: t('connectorId'),
    //   accessorKey: 'connectorId',
    //   sortAllow: false,
    // },
    {
      label: t('gatewayTransactionId'),
      accessorKey: 'gatewayTransactionId',
      sortAllow: false,
    },
    // {
    //   label: t('linkedAt'),
    //   accessorKey: 'linkedAt',
    //   sortAllow: true,
    //   /**
    //    * Custom formatter for Linked At date.
    //    * @param {PowerBankPreauthInterface} row - Row data.
    //    * @returns The formatted date element.
    //    */
    //   customData: (row: PowerBankPreauthInterface) => <span>{formatDateTime(row.linkedAt)}</span>,
    // },
    {
      label: t('capturedAt'),
      accessorKey: 'capturedAt',
      sortAllow: true,
      /**
       * Custom formatter for Captured At date.
       * @param {PowerBankPreauthInterface} row - Row data.
       * @returns The formatted date element.
       */
      customData: (row: PowerBankPreauthInterface) => <span>{formatDateTime(row.capturedAt)}</span>,
    },
    {
      label: t('expiresAt'),
      accessorKey: 'expiresAt',
      sortAllow: true,
      /**
       * Custom formatter for Expires At date.
       * @param {PowerBankPreauthInterface} row - Row data.
       * @returns The formatted date element.
       */
      customData: (row: PowerBankPreauthInterface) => <span>{formatDateTime(row.expiresAt)}</span>,
    },
    {
      label: t('Created'),
      accessorKey: 'createdAt',
      sortAllow: true,
      /**
       * Custom formatter for Created At date.
       * @param {PowerBankPreauthInterface} row - Row data.
       * @returns The formatted date element.
       */
      customData: (row: PowerBankPreauthInterface) => <span>{formatDateTime(row.createdAt)}</span>,
    },
    {
      label: t('Last Updated'),
      accessorKey: 'updatedAt',
      sortAllow: true,
      /**
       * Custom formatter for Updated At date.
       * @param {PowerBankPreauthInterface} row - Row data.
       * @returns The formatted date element.
       */
      customData: (row: PowerBankPreauthInterface) => <span>{formatDateTime(row.updatedAt)}</span>,
    },
  ];

  /**
   * Current table columns definitions for export mapping.
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('referenceId'), accessorKey: 'internalRef' },
    { label: t('pspPreauthId'), accessorKey: 'pspPreauthId' },
    { label: t('paymentMethod'), accessorKey: 'paymentMethod' },
    { label: t('tokenUid'), accessorKey: 'tokenUid' },
    { label: t('amount'), accessorKey: 'amount' },
    {
      label: t('capturedAmount'),
      accessorKey: 'capturedAmount',
    },
    { label: t('status'), accessorKey: 'status' },
    // { label: t('sessionId'), accessorKey: 'cpoSessionId' },
    // { label: t('locationId'), accessorKey: 'locationId' },
    // { label: t('evseUid'), accessorKey: 'evseUid' },
    // { label: t('connectorId'), accessorKey: 'connectorId' },
    {
      label: t('gatewayTransactionId'),
      accessorKey: 'gatewayTransactionId',
    },
    // { label: t('linkedAt'), accessorKey: 'linkedAt' },
    { label: t('capturedAt'), accessorKey: 'capturedAt' },
    { label: t('expiresAt'), accessorKey: 'expiresAt' },
    { label: t('Created'), accessorKey: 'createdAt' },
    { label: t('Last Updated'), accessorKey: 'updatedAt' },
  ];

  /**
   * Handle pagination page or limit change.
   * @param {any} value - The pagination value containing page and limit.
   */
  const pageNateLimitChange = (value: any) => {
    if (value?.page) {
      pageReference.current = value.page;
    }
    const queryParameters = GetParametersFromCommonFilters(filters);
    const sortValue = sortField && sortBy ? { sortField, sortBy } : {};
    fetchPreauths({
      ...value,
      ...queryParameters,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      ...sortValue,
    });
  };

  /**
   * Active export columns filtering out hidden columns.
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export table data to PDF or CSV format.
   * @param {string} type - File type ('csv' or 'pdf').
   */
  const exportDataPdfOrCSV = (type: string) => {
    if (
      dateRange.startDate &&
      dateRange.endDate &&
      getDaysBetween(dateRange.startDate, dateRange.endDate) > 31
    ) {
      dispatch(
        setNotificationMessage({
          message: t('dateRangeLimitMessage'),
          status: true,
          type: 'error',
          code: 400,
        }),
      );
      return;
    }

    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload: any = {
      ...queryParameters,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    };
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;

    const apiCall = powerBankPreauthApi.fetchPreauthExport(removeEmptyKeys(payload));
    apiCall
      .then((response) => {
        const rawResults = response?.data?.results || response?.data || [];
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = rawResults.map((row: PowerBankPreauthInterface, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'amount':
                return `${ToFixedValue(row.amount)} ${row.currency || 'SAR'}`;
              case 'capturedAmount':
                return row.capturedAmount != null
                  ? `${ToFixedValue(row.capturedAmount)} ${row.currency || 'SAR'}`
                  : '-';
              //   case 'linkedAt':
              //     return formatDateTime(row.linkedAt);
              case 'capturedAt':
                return formatDateTime(row.capturedAt);
              case 'expiresAt':
                return formatDateTime(row.expiresAt);
              case 'createdAt':
                return formatDateTime(row.createdAt);
              case 'updatedAt':
                return formatDateTime(row.updatedAt);
              default:
                return (row as any)[col.accessorKey] || t('na');
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'powerbank-preauth-payments.csv');
          } else {
            exportPDF(headers, rows, 'powerbank-preauth-payments.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || 'Export failed',
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Handle table header column sort change.
   * @param {any} sortData - Sort payload containing sortField and sortBy.
   */
  const handleSortFunction = (sortData: any) => {
    pageReference.current = DefaultApiLimit.page;
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchPreauths({
      ...queryParameters,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  const isFirstMountFilters = React.useRef(true);

  useEffect(() => {
    if (isFirstMountFilters.current) {
      isFirstMountFilters.current = false;
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchPreauths(
        {
          page: DefaultApiLimit.page,
          limit,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          ...queryParameters,
        },
        true,
      );
      fetchStatusCounts();
      return;
    }

    pageReference.current = DefaultApiLimit.page;
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchPreauths(
      {
        page: DefaultApiLimit.page,
        limit,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        ...queryParameters,
      },
      true,
    );
    fetchStatusCounts();

    if (isFilterUpdated) {
      dispatch(disablePreauthFilterApplied());
    }
  }, [filters, dateRange.startDate, dateRange.endDate]);

  useEffect(() => {
    dispatch(
      setPageTitle({
        title: 'PowerBank Payment Requests',
        icon: <TransactionIcon />,
        count: totalResults,
      }),
    );
  }, [totalResults, t]);

  useEffect(() => {
    if (searchParameters.get('sessionId')) {
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('sessionId');
      setSearchParameters(parameters, { replace: true });
    }
  }, []);

  return (
    <div className="payment-management-wrap">
      <div className="stats-bar">
        <div className="stat-item" role="presentation" onClick={handleTotalCardClick}>
          <span className="stat-label">{t('total')}</span>
          <span className="stat-value">{statusCounts?.total ?? totalResults ?? 0}</span>
        </div>
        <div className="stat-divider" />
        <button
          type="button"
          className={`stat-badge reserved-badge ${isStatusCardActive('RESERVED') ? 'active' : ''}`}
          onClick={() => handleStatusCardClick('status', 'RESERVED')}>
          <span className="badge-dot" />
          {t('reserved')}: {statusCounts?.counts?.RESERVED ?? 0}
        </button>
        <button
          type="button"
          className={`stat-badge linked-badge ${isStatusCardActive('LINKED') ? 'active' : ''}`}
          onClick={() => handleStatusCardClick('status', 'LINKED')}>
          <span className="badge-dot" />
          {t('linked')}: {statusCounts?.counts?.LINKED ?? 0}
        </button>
        <button
          type="button"
          className={`stat-badge captured-badge ${isStatusCardActive('CAPTURED') ? 'active' : ''}`}
          onClick={() => handleStatusCardClick('status', 'CAPTURED')}>
          <span className="badge-dot" />
          {t('captured')}: {statusCounts?.counts?.CAPTURED ?? 0}
        </button>
        <button
          type="button"
          className={`stat-badge released-badge ${isStatusCardActive('RELEASED') ? 'active' : ''}`}
          onClick={() => handleStatusCardClick('status', 'RELEASED')}>
          <span className="badge-dot" />
          {t('released')}: {statusCounts?.counts?.RELEASED ?? 0}
        </button>
        <button
          type="button"
          className={`stat-badge orphaned-badge ${isStatusCardActive('ORPHANED') ? 'active' : ''}`}
          onClick={() => handleStatusCardClick('status', 'ORPHANED')}>
          <span className="badge-dot" />
          {t('orphaned')}: {statusCounts?.counts?.ORPHANED ?? 0}
        </button>
      </div>
      <div className="table-content">
        <CustomTable
          dataKey="PowerBankPreauth"
          version="v2"
          header={column}
          data={preauths}
          limit={limit}
          page={pageReference.current || page || 1}
          totalPages={totalPages}
          totalResults={totalResults}
          loading={loading}
          serialNo
          sortData={{ sortBy, sortField }}
          handleSortChange={handleSortFunction}
          pageLimitChange={pageNateLimitChange}
          onHiddenColumnsChange={setHiddenColumns}
          sizeChangeButtonRequired
          filters={filters}
          filtersToDispatch={updatePreauthFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchPowerBankPreauthFilters}
          refreshRequired
          handleRefresh={handleRefresh}
          refershCurrentPages={handleRefresh}
          exportDataPdfOrCSV={
            permissionCheck('mobilityTransactionsExport') ? exportDataPdfOrCSV : undefined
          }
          exportLoader={exportLoader}
          hoverLabel={t('refresh')}
          rightTopContent={renderDateFilters()}
          onClearFilterChange={() => {
            clearDateRange();
          }}
          sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
          onSidePanelClose={handleSidePanelClose}
        />
      </div>
    </div>
  );
}

export default PowerBankRequest;
